// Enhanced PDF Configuration with Polish characters support
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertPolishChars } from '../../utils/pdfFonts';

// Extend jsPDF type globally
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

// Export configured jsPDF class
export { jsPDF };

// Enhanced table configuration with better typography and responsive widths
export const defaultAutoTableConfig = {
  theme: 'grid' as const,
  styles: {
    fontSize: 9,  // Smaller font to fit more content
    cellPadding: 3,  // Reduced padding
    font: 'helvetica',
    textColor: [40, 40, 40],
    lineColor: [220, 220, 220],
    lineWidth: 0.2,
    overflow: 'linebreak' as const,
    halign: 'left' as const,
    valign: 'middle' as const,
  },
  headStyles: {
    fillColor: [255, 165, 0], // Orange theme color
    textColor: [255, 255, 255],
    fontStyle: 'bold' as const,
    halign: 'center' as const,
    fontSize: 11,
  },
  bodyStyles: {
    halign: 'left' as const,
  },
  alternateRowStyles: {
    fillColor: [250, 250, 250],
  },
  margin: { 
    left: 15, 
    right: 15,
    top: 10,
    bottom: 10,
  },
  tableLineColor: [200, 200, 200],
  tableLineWidth: 0.1,
};

// Safe text conversion for Polish characters
export const safePdfText = (text: any, defaultValue: string = ''): string => {
  if (text === null || text === undefined) {
    return convertPolishChars(defaultValue);
  }
  
  const stringValue = typeof text === 'object' ? JSON.stringify(text) : String(text);
  return convertPolishChars(stringValue);
};

// Enhanced title page generator with company data
export const createEnhancedTitlePage = (
  doc: jsPDF,
  title: string,
  subtitle: string,
  projectName?: string,
  clientName?: string
): number => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Light gradient background at top only
  for (let i = 0; i < 15; i++) {
    const gray = 255 - i * 3;
    doc.setFillColor(gray, gray, gray);
    doc.rect(0, i * 2, pageWidth, 2, 'F');
  }
  
  // Logo section - smaller and higher
  const logoY = 20;  // Changed from 40 to 20 - moved up
  const logoWidth = 50;  // Reduced from 70
  const logoHeight = 18;  // Reduced from 25
  
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageWidth / 2 - logoWidth/2 - 5, logoY - 3, logoWidth + 10, logoHeight + 6, 2, 2, 'F');
  
  // Logo with gradient effect (simulated) - smaller
  doc.setFillColor(255, 165, 0);
  doc.roundedRect(pageWidth / 2 - logoWidth/2, logoY, logoWidth, logoHeight, 2, 2, 'F');
  
  // Logo text - smaller font
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);  // Reduced from 18
  doc.setFont('helvetica', 'bold');
  doc.text('PLEXISYSTEM', pageWidth / 2, logoY + 11, { align: 'center' });
  
  // Main title with better spacing
  let currentY = 55;  // Changed from 100 to 55 - moved up
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(22);  // Reduced from 26
  doc.setFont('helvetica', 'bold');
  // Keep Polish text but without special characters
  const cleanTitle = title
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ź/g, 'z')
    .replace(/ż/g, 'z')
    .replace(/Ą/g, 'A')
    .replace(/Ć/g, 'C')
    .replace(/Ę/g, 'E')
    .replace(/Ń/g, 'N')
    .replace(/Ó/g, 'O')
    .replace(/Ś/g, 'S')
    .replace(/Ź/g, 'Z')
    .replace(/Ż/g, 'Z');
  const titleLines = doc.splitTextToSize(cleanTitle, pageWidth - 60);
  titleLines.forEach((line: string) => {
    doc.text(line, pageWidth / 2, currentY, { align: 'center' });
    currentY += 9;  // Reduced spacing
  });
  
  // Subtitle
  currentY += 3;
  doc.setFontSize(14);  // Reduced from 16
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const cleanSubtitle = subtitle
    .replace(/ł/g, 'l')
    .replace(/ó/g, 'o')
    .replace(/ą/g, 'a')
    .replace(/ę/g, 'e')
    .replace(/ć/g, 'c')
    .replace(/ń/g, 'n')
    .replace(/ś/g, 's')
    .replace(/ź/g, 'z')
    .replace(/ż/g, 'z');
  const subtitleLines = doc.splitTextToSize(cleanSubtitle, pageWidth - 60);
  subtitleLines.forEach((line: string) => {
    doc.text(line, pageWidth / 2, currentY, { align: 'center' });
    currentY += 7;  // Reduced spacing
  });
  
  // Decorative line
  currentY += 8;
  doc.setDrawColor(255, 165, 0);
  doc.setLineWidth(0.5);
  doc.line(60, currentY, pageWidth - 60, currentY);
  
  // Project info box
  if (projectName || clientName) {
    currentY += 12;
    doc.setFillColor(248, 248, 248);
    doc.roundedRect(45, currentY, pageWidth - 90, 35, 3, 3, 'F');  // Reduced height from 45 to 35
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(45, currentY, pageWidth - 90, 35, 3, 3);
    
    currentY += 12;
    if (projectName) {
      doc.setFontSize(14);  // Reduced from 16
      doc.setTextColor(255, 165, 0);
      doc.setFont('helvetica', 'bold');
      const cleanProjectName = projectName
        .replace(/ó/g, 'o')
        .replace(/ł/g, 'l');
      doc.text(cleanProjectName, pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;
    }
    
    if (clientName) {
      doc.setFontSize(12);  // Reduced from 13
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(`Klient: ${clientName}`, pageWidth / 2, currentY, { align: 'center' });
    }
  }
  
  // Date and time in a modern format - moved lower
  const now = new Date();
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Wygenerowano: ${now.toLocaleDateString('pl-PL')} o ${now.toLocaleTimeString('pl-PL')}`,
    pageWidth / 2,
    pageHeight - 60,  // Changed from -80 to -60
    { align: 'center' }
  );
  
  // Company footer with REAL company data
  doc.setFontSize(9);  // Reduced from 10
  doc.setTextColor(150, 150, 150);
  doc.text('PPHU Plexi System s.c.', pageWidth / 2, pageHeight - 40, { align: 'center' });
  doc.text('ul. Ks. Dr. Leona Heyke 11, 84-206 Nowy Dwor Wejherowski', pageWidth / 2, pageHeight - 35, { align: 'center' });
  doc.text('tel: +48 884 042 107 | www.plexisystem.pl', pageWidth / 2, pageHeight - 30, { align: 'center' });
  
  return currentY + 45;  // Reduced from 60
};

// Section header with modern design
export const addModernSectionHeader = (
  doc: jsPDF,
  title: string,
  y: number,
  color: [number, number, number] = [255, 165, 0]
): number => {
  const pageWidth = doc.internal.pageSize.width;
  
  // Background bar
  doc.setFillColor(...color);
  doc.rect(0, y - 5, pageWidth, 12, 'F');
  
  // Title text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(safePdfText(title), 15, y + 2);
  
  return y + 15;
};

// Enhanced page header
export const addEnhancedPageHeader = (doc: jsPDF, title: string = 'RAPORT TECHNICZNY'): void => {
  const pageWidth = doc.internal.pageSize.width;
  
  // Header background
  doc.setFillColor(250, 250, 250);
  doc.rect(0, 0, pageWidth, 20, 'F');
  
  // Logo
  doc.setFillColor(255, 165, 0);
  doc.rect(10, 5, 30, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('PLEXISYSTEM', 25, 11, { align: 'center' });
  
  // Title
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(safePdfText(title), pageWidth / 2, 11, { align: 'center' });
  
  // Date
  doc.setTextColor(150, 150, 150);
  doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString('pl-PL'), pageWidth - 15, 11, { align: 'right' });
};

// Enhanced page footer
export const addEnhancedPageFooter = (doc: jsPDF, pageNumber?: number): void => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const footerY = pageHeight - 15;
  
  // Footer line
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5);
  
  // Footer text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  
  // Left - Generated date
  doc.text(
    safePdfText(`Wygenerowano: ${new Date().toLocaleDateString('pl-PL')}`),
    15,
    footerY
  );
  
  // Center - Page number
  if (pageNumber) {
    doc.text(
      safePdfText(`Strona ${pageNumber}`),
      pageWidth / 2,
      footerY,
      { align: 'center' }
    );
  }
  
  // Right - Website
  doc.text(
    'www.plexisystem.pl',
    pageWidth - 15,
    footerY,
    { align: 'right' }
  );
};

// Key-value info box with better layout
export const addInfoBox = (
  doc: jsPDF,
  data: Array<[string, string]>,
  x: number,
  y: number,
  width: number,
  height: number,
  title?: string
): number => {
  // Box background
  doc.setFillColor(248, 248, 248);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, height, 2, 2);
  
  let currentY = y + 8;
  
  // Title if provided
  if (title) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 165, 0);
    doc.text(safePdfText(title), x + 5, currentY);
    currentY += 8;
  }
  
  // Data rows
  doc.setFontSize(10);
  data.forEach(([label, value]) => {
    // Label
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text(safePdfText(label + ':'), x + 5, currentY);
    
    // Value
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text(safePdfText(value), x + width / 2, currentY);
    
    currentY += 6;
  });
  
  return currentY;
};

// Status indicator with modern design
export const addStatusIndicator = (
  doc: jsPDF,
  status: boolean,
  message: string,
  x: number,
  y: number,
  width: number
): void => {
  const color = status ? [0, 150, 0] : [200, 0, 0];
  const icon = status ? '[OK]' : '[X]';
  
  // Background
  doc.setFillColor(...(color as [number, number, number]));
  doc.setDrawColor(...(color as [number, number, number]));
  doc.roundedRect(x, y, width, 25, 3, 3, 'FD');
  
  // Icon circle
  doc.setFillColor(255, 255, 255);
  doc.circle(x + 15, y + 12.5, 8, 'F');
  
  // Icon text
  doc.setTextColor(...(color as [number, number, number]));
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(icon, x + 15, y + 13, { align: 'center' });
  
  // Message
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text(safePdfText(message), x + width / 2, y + 13, { align: 'center' });
};
